import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import CategoryEditView from "./Categorieseditview";

import {
  useDeleteCategoryByIdMutation,
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

import { IsValidPermissions } from "Routes/AuthProtected";

interface Category {
  id: number;
  name: string;
  description?: string | null;
  account_number?: string | null;
  created_at: string;
  modified_at?: string | null;
}

const CategoriesList: React.FC = () => {
  // local UI state
  const [data, setData] = useState<Category[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState<number | null>(
    null
  );

  // view modal state
  const [viewCategoryId, setViewCategoryId] = useState<number | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [pageState, setPageState] = useState({ page: 1, pageSize: 30, search: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const debounce = window.setTimeout(() => {
      setPageState((previous) => ({ ...previous, page: 1, search: searchInput.trim() }));
    }, 300);
    return () => window.clearTimeout(debounce);
  }, [searchInput]);

  // list hook
  const {
    data: categoriesResponse,
    isLoading: loadingCategories,
    refetch: refetchCategories,
  } = useGetCategoriesQuery(pageState);
  const [deleteCategory] = useDeleteCategoryByIdMutation();

  // detail hook for view modal (skip when no id)
  const { data: categoryDetail, isFetching: fetchingCategoryDetail } =
    useGetCategoryByIdQuery(viewCategoryId ?? undefined, {
      skip: !viewCategoryId,
    });

  // keep local copy sorted by id ascending
  useEffect(() => {
    if (categoriesResponse?.results) {
      const sorted = [...categoriesResponse.results].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setData(sorted);
    } else {
      setData([]);
    }
  }, [categoriesResponse]);

  // handlers for edit/create modal
  const openCreateModal = () => {
    setCurrentCategoryId(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const openEditModal = (id: number) => {
    setCurrentCategoryId(id);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentCategoryId(null);
    setIsEditMode(false);
  };

  const handleSaved = () => {
    // close edit modal and refresh list
    closeEditModal();
    refetchCategories();
  };

  // handlers for view modal
  const openViewModal = (id: number) => {
    setViewCategoryId(id);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewCategoryId(null);
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Delete category "${category.name}"? This cannot be undone.`)) return;
    try {
      await deleteCategory(category.id).unwrap();
      toast.success("Category deleted successfully.");
      refetchCategories();
    } catch (error: any) {
      toast.error(error?.data?.detail || error?.data?.error || "Failed to delete category.");
    }
  };

  const baseColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      {
        Header: "Name",
        accessor: "name",
        Cell: (row: any) => (
          <span className="text-body">{row.row.original.name}</span>
        ),
      },
      {
        Header: "Priority",
        accessor: "priority",
        Cell: (row: any) => (
          <span className="text-body">{row.row.original.priority}</span>
        ),
      },
      {
        Header: "Account Number",
        accessor: "account_number",
        Cell: (cell: any) =>
          cell.row.original.account_number ? (
            <span className="text-body">{cell.row.original.account_number}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: (cell: any) =>
          cell.row.original.description ? (
            <span>{cell.row.original.description}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (cell: any) =>
          new Date(cell.row.original.created_at).toLocaleDateString(),
      },
      {
        Header: "Modified",
        accessor: "modified_at",
        Cell: (cell: any) =>
          cell.row.original.modified_at
            ? new Date(cell.row.original.modified_at).toLocaleDateString()
            : "-",
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const c: Category = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                {(IsValidPermissions(["VIEW_ALL_INVENTORY_CATEGORY"]) || IsValidPermissions(["VIEW_ONLY_INVENTORY_CATEGORY"])) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal(c?.id);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted" />{" "}
                    View
                  </DropdownItem>
                }
                {IsValidPermissions(["EDIT_INVENTORY_CATEGORY"]) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openEditModal(c.id);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted" />{" "}
                    Edit
                  </DropdownItem>
                }
                {IsValidPermissions(["EDIT_INVENTORY_CATEGORY"]) && (
                  <DropdownItem className="text-danger" onClick={() => handleDelete(c)}>
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  // Quick fix: mark columns non-sortable by default (so header has no sort toggle)
  const columns = useMemo(
    () => baseColumns.map((c) => ({ ...c, disableSortBy: true })),
    [baseColumns]
  );

  document.title = "Categories | Resource Inventory";

  if (loadingCategories) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Categories" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Categories" pageTitle="Resource Inventory" />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Categories</h5>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search categories..."
                    aria-label="Search categories"
                  />
                  {IsValidPermissions(["ADD_INVENTORY_CATEGORY"]) &&
                    <Button color="primary" onClick={openCreateModal}>
                      Add Category
                    </Button>
                  }
                </div>
              </div>

              <div className="card-body pt-0">
                {data && data?.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={data}
                    isGlobalFilter={false}
                    customPageSize={pageState.pageSize}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search Categories..."
                    isPagination={false}
                    isServerPagination
                    serverPaginationProps={categoriesResponse}
                    setPageState={setPageState}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <i className="ri-search-line display-5 text-success" />
                    </div>
                    <div className="mt-4">
                      <h5>Sorry! No Categories Found</h5>
                      <p className="text-muted">
                        Click "Add Category" to create one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit/Create Modal — NO ModalFooter */}
        <Modal isOpen={showEditModal} toggle={closeEditModal} size="lg">
          <ModalHeader toggle={closeEditModal}>
            {isEditMode ? "Edit Category" : "Create Category"}
          </ModalHeader>
          <ModalBody>
            <CategoryEditView
              categoryId={currentCategoryId ?? undefined}
              compact={true}
              onSaved={handleSaved}
            />
          </ModalBody>
          {/* intentionally no ModalFooter here */}
        </Modal>

        {/* View Modal */}
        <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
          <ModalHeader toggle={closeViewModal}>Category Details</ModalHeader>
          <ModalBody>
            {fetchingCategoryDetail ? (
              <div className="text-center py-4">
                <Loader />
              </div>
            ) : categoryDetail ? (
              <div className="table-card">
                <table className="table mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium">Name</td>
                      <td>{categoryDetail.name}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Account Number</td>
                      <td>{categoryDetail.account_number ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Description</td>
                      <td>{categoryDetail.description ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Created</td>
                      <td>
                        {new Date(categoryDetail?.created_at).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Modified</td>
                      <td>
                        {categoryDetail?.modified_at
                          ? new Date(
                            categoryDetail.modified_at
                          ).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default CategoriesList;
